import React, {useEffect, useMemo, useState} from 'react';
import {
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import {alpha} from '@mui/material/styles';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {useNavigate} from 'react-router-dom';
import {enqueueSnackbar} from 'notistack';
import {logError} from '../../shared/services/logger.service';
import {SavaquanHttpError} from '../../shared/utils/http-error';
import {
  PermissionCatalogItem,
  RbacCompanyUserDto,
  RbacGroupDto,
  RbacRoleDto,
} from '../../shared/permissions/permissions';
import {CompanyUserAssignmentDto, HoursWorkedWeek} from './dtos/company-user-assignment.dto';
import {
  addCompanyUserAssignment,
  deleteCompanyUserAssignment,
  sendCompanyUserAssignmentInvite,
  setCompanyUserAssignmentActive,
  updateCompanyUserAssignment,
} from './company-user-assignment.service';
import {deleteRbacUser, setRbacUserActive, updateRbacUser} from './rbac.service';
import AddressAutocompleteField, {ParsedAddressFields} from '../../shared/components/AddressAutocompleteField';
import {nationalPhoneDigits} from '../../shared/phone';

const AREA_LABELS: Record<string, string> = {
  clients: 'Clients',
  appointments: 'Estimate Appointments',
  estimates: 'Estimates',
  jobs: 'Jobs',
  invoices: 'Invoicing',
  team: 'Team Management',
  settings: 'Settings',
  equipment: 'Equipment',
  rbac: 'Team & Access',
  library: 'Comparable Library',
};

const AREA_ORDER = Object.keys(AREA_LABELS);

const areaLabel = (area: string): string =>
  AREA_LABELS[area] ?? area.replace(/_/g, ' ');

const groupLevelNumber = (group: RbacGroupDto): number => {
  const match = group.systemKey?.match(/(\d+)/);
  return match ? Number(match[1]) : 999;
};

const parseGroupLabel = (name: string): {title: string; detail: string | null} => {
  const match = name.match(/^(Level\s+\d+)\s*\((.+)\)\s*$/i);
  if (!match) {
    return {title: name, detail: null};
  }
  return {title: match[1], detail: `(${match[2]})`};
};

export type PersonEditorTarget =
  | {kind: 'new'}
  | {kind: 'user'; user: RbacCompanyUserDto}
  | {kind: 'invite'; invite: CompanyUserAssignmentDto};

export interface PersonDetailProps {
  target: PersonEditorTarget;
  groups: RbacGroupDto[];
  roles: RbacRoleDto[];
  permissions: PermissionCatalogItem[];
  currentUserId: number | null;
  onClose: () => void;
  onSaved: () => void;
  /** The profile is the page (SQ-599), not a pop-up: the card has no frame of its own. */
  page?: boolean;
}

const splitName = (full: string): {first: string; last: string} => {
  const trimmed = full.trim();
  const space = trimmed.indexOf(' ');
  if (space === -1) {
    return {first: trimmed, last: ''};
  }
  return {first: trimmed.slice(0, space), last: trimmed.slice(space + 1)};
};

const joinName = (first: string, last: string): string =>
  [first, last].map(part => part.trim()).filter(Boolean).join(' ');

const groupIdsForUserSave = (
  currentGroupIds: number[],
  selectedGroupId: number,
  displayedGroupId: number | null,
): number[] | undefined => {
  if (selectedGroupId === displayedGroupId) {
    return undefined;
  }

  return [
    selectedGroupId,
    ...currentGroupIds.filter(id => id !== displayedGroupId && id !== selectedGroupId),
  ];
};

const toDateInputValue = (value?: string | null): string => {
  if (!value) {
    return '';
  }
  return value.slice(0, 10);
};

const emptyToNull = (value: string): string | null => {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const formatPhoneNumber = (value: string): string => {
  const cleaned = nationalPhoneDigits(value);
  const match = cleaned.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);
  if (!match) {
    return value;
  }
  const part1 = match[1] ? `(${match[1]}` : '';
  const part2 = match[2] ? `) ${match[2]}` : '';
  const part3 = match[3] ? `-${match[3]}` : '';
  return `${part1}${part2}${part3}`;
};

const US_STATES: {code: string; name: string}[] = [
  {code: 'AL', name: 'Alabama'},
  {code: 'AK', name: 'Alaska'},
  {code: 'AZ', name: 'Arizona'},
  {code: 'AR', name: 'Arkansas'},
  {code: 'CA', name: 'California'},
  {code: 'CO', name: 'Colorado'},
  {code: 'CT', name: 'Connecticut'},
  {code: 'DE', name: 'Delaware'},
  {code: 'DC', name: 'District of Columbia'},
  {code: 'FL', name: 'Florida'},
  {code: 'GA', name: 'Georgia'},
  {code: 'HI', name: 'Hawaii'},
  {code: 'ID', name: 'Idaho'},
  {code: 'IL', name: 'Illinois'},
  {code: 'IN', name: 'Indiana'},
  {code: 'IA', name: 'Iowa'},
  {code: 'KS', name: 'Kansas'},
  {code: 'KY', name: 'Kentucky'},
  {code: 'LA', name: 'Louisiana'},
  {code: 'ME', name: 'Maine'},
  {code: 'MD', name: 'Maryland'},
  {code: 'MA', name: 'Massachusetts'},
  {code: 'MI', name: 'Michigan'},
  {code: 'MN', name: 'Minnesota'},
  {code: 'MS', name: 'Mississippi'},
  {code: 'MO', name: 'Missouri'},
  {code: 'MT', name: 'Montana'},
  {code: 'NE', name: 'Nebraska'},
  {code: 'NV', name: 'Nevada'},
  {code: 'NH', name: 'New Hampshire'},
  {code: 'NJ', name: 'New Jersey'},
  {code: 'NM', name: 'New Mexico'},
  {code: 'NY', name: 'New York'},
  {code: 'NC', name: 'North Carolina'},
  {code: 'ND', name: 'North Dakota'},
  {code: 'OH', name: 'Ohio'},
  {code: 'OK', name: 'Oklahoma'},
  {code: 'OR', name: 'Oregon'},
  {code: 'PA', name: 'Pennsylvania'},
  {code: 'RI', name: 'Rhode Island'},
  {code: 'SC', name: 'South Carolina'},
  {code: 'SD', name: 'South Dakota'},
  {code: 'TN', name: 'Tennessee'},
  {code: 'TX', name: 'Texas'},
  {code: 'UT', name: 'Utah'},
  {code: 'VT', name: 'Vermont'},
  {code: 'VA', name: 'Virginia'},
  {code: 'WA', name: 'Washington'},
  {code: 'WV', name: 'West Virginia'},
  {code: 'WI', name: 'Wisconsin'},
  {code: 'WY', name: 'Wyoming'},
];

const WEEKDAYS: {value: string; label: string}[] = [
  {value: 'monday', label: 'Monday'},
  {value: 'tuesday', label: 'Tuesday'},
  {value: 'wednesday', label: 'Wednesday'},
  {value: 'thursday', label: 'Thursday'},
  {value: 'friday', label: 'Friday'},
  {value: 'saturday', label: 'Saturday'},
  {value: 'sunday', label: 'Sunday'},
];

interface HoursWorkedDayForm {
  day: string;
  hours: string;
}

interface HoursWorkedWeekForm {
  startDate: string;
  days: HoursWorkedDayForm[];
}

const toIsoDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const parseLocalDate = (value: string): Date | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) {
    return null;
  }
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
};

const addDaysIso = (iso: string, days: number): string => {
  const date = parseLocalDate(iso);
  if (!date) {
    return iso;
  }
  date.setDate(date.getDate() + days);
  return toIsoDate(date);
};

const mondayOf = (value: string | Date): string => {
  const date = typeof value === 'string'
    ? parseLocalDate(value)
    : new Date(value.getFullYear(), value.getMonth(), value.getDate());
  if (!date) {
    return toIsoDate(new Date());
  }
  const day = date.getDay();
  const offset = day === 0 ? -6 : 1 - day;
  date.setDate(date.getDate() + offset);
  return toIsoDate(date);
};

const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const formatShortDate = (iso: string): string => {
  const date = parseLocalDate(iso);
  if (!date) {
    return iso;
  }
  return `${SHORT_MONTHS[date.getMonth()]} ${date.getDate()}`;
};

const formatWeekRange = (startDate: string): string => {
  if (!startDate) {
    return 'Week';
  }
  const start = mondayOf(startDate);
  const end = addDaysIso(start, 6);
  const startParsed = parseLocalDate(start);
  const year = startParsed?.getFullYear();
  return `${formatShortDate(start)} – ${formatShortDate(end)}${year ? `, ${year}` : ''}`;
};

const weekOverlapsRange = (week: HoursWorkedWeekForm, fromDate: string, toDate: string): boolean => {
  if (!fromDate && !toDate) {
    return true;
  }
  if (!week.startDate) {
    return true;
  }
  const weekStart = mondayOf(week.startDate);
  const weekEnd = addDaysIso(weekStart, 6);
  if (fromDate && weekEnd < fromDate) {
    return false;
  }
  if (toDate && weekStart > toDate) {
    return false;
  }
  return true;
};

const nextWeekStart = (weeks: HoursWorkedWeekForm[]): string => {
  const used = new Set(weeks.map(week => week.startDate).filter(Boolean));
  let start = weeks.reduce((latest, week) => (
    week.startDate && week.startDate > latest ? week.startDate : latest
  ), mondayOf(new Date()));
  start = weeks.some(week => week.startDate === start) ? addDaysIso(start, 7) : start;
  while (used.has(start)) {
    start = addDaysIso(start, 7);
  }
  return start;
};

const emptyWeek = (startDate = mondayOf(new Date())): HoursWorkedWeekForm => ({
  startDate,
  days: WEEKDAYS.map(weekday => ({day: weekday.value, hours: ''})),
});

const toHoursWorkedForm = (weeks?: HoursWorkedWeek[] | null): HoursWorkedWeekForm[] => {
  if (!weeks || weeks.length === 0) {
    return [emptyWeek()];
  }
  let nextStart = mondayOf(new Date());
  return weeks.map(week => {
    const startDate = week.startDate
      ? mondayOf(toDateInputValue(week.startDate))
      : nextStart;
    nextStart = addDaysIso(startDate, 7);
    const hoursByDay = new Map(
      (week.days ?? []).map(day => [day.day.toLowerCase(), day.hours == null ? '' : String(day.hours)]),
    );
    return {
      startDate,
      days: WEEKDAYS.map(weekday => ({
        day: weekday.value,
        hours: hoursByDay.get(weekday.value) ?? '',
      })),
    };
  });
};

const serializeHoursWorked = (weeks: HoursWorkedWeekForm[]): HoursWorkedWeek[] =>
  weeks
    .map(week => ({
      startDate: week.startDate || null,
      days: week.days
        .filter(day => day.day && day.hours.trim())
        .map(day => ({day: day.day, hours: Number(day.hours) || 0})),
    }))
    .filter(week => week.days.length > 0);

const weekdayLabel = (value: string): string =>
  WEEKDAYS.find(day => day.value === value)?.label ?? value;

const formatHoursLabel = (hours: string | number): string => {
  const parsed = typeof hours === 'number' ? hours : Number(hours);
  if (!Number.isFinite(parsed) || (typeof hours === 'string' && !hours.trim())) {
    return '0 hrs';
  }
  return `${parsed} hr${parsed === 1 ? '' : 's'}`;
};

const moneyToInput = (value?: number | null): string => (value == null ? '' : String(value));

const parseMoney = (value: string): number | null => {
  const trimmed = value.trim().replace(/[$,]/g, '');
  if (!trimmed) {
    return null;
  }
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
};

const errorMessage = (error: unknown): string => {
  if (error instanceof SavaquanHttpError) {
    return error.details || error.message;
  }
  return String(error);
};

const phoneDigitCount = (value: string): number => nationalPhoneDigits(value).length;

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'background.paper',
    borderRadius: '7px',
    '& fieldset': {borderColor: 'divider'},
    '&:hover fieldset': {borderColor: 'primary.main'},
    '&.Mui-focused fieldset': {borderColor: 'primary.main', borderWidth: 2},
  },
  '& .MuiInputLabel-root.Mui-focused': {color: 'primary.main'},
};

const PersonDetailComponent: React.FC<PersonDetailProps> = React.forwardRef((props, ref) => {
  const navigate = useNavigate();
  const existingUser = props.target.kind === 'user' ? props.target.user : null;
  const existingInvite = props.target.kind === 'invite' ? props.target.invite : null;
  const isNew = props.target.kind === 'new';
  const isSignedIn = props.target.kind === 'user';
  const isSelf = existingUser != null && existingUser.id === props.currentUserId;
  const isActive = existingUser
    ? existingUser.isActive !== false
    : existingInvite
      ? existingInvite.isActive !== false
      : true;
  const canDelete = existingUser ? existingUser.canDelete !== false : true;
  const membershipStatus = !isActive ? 'Inactive' : isSignedIn ? 'Active' : 'Pending';

  const sortedGroups = useMemo(
    () => [...props.groups].sort((a, b) => groupLevelNumber(a) - groupLevelNumber(b)),
    [props.groups],
  );

  const initialName = splitName(existingUser?.name ?? existingInvite?.name ?? '');
  const initialGroupId = existingUser?.groupIds[0]
    ?? existingInvite?.defaultGroupId
    ?? null;

  const [firstName, setFirstName] = useState(initialName.first);
  const [lastName, setLastName] = useState(initialName.last);
  const [email, setEmail] = useState(existingUser?.email ?? existingInvite?.email ?? '');
  const [phoneNumber, setPhoneNumber] = useState(
    formatPhoneNumber(existingUser?.phoneNumber ?? existingInvite?.phoneNumber ?? ''),
  );
  const [address, setAddress] = useState(existingUser?.address ?? existingInvite?.address ?? '');
  const [city, setCity] = useState(existingUser?.city ?? existingInvite?.city ?? '');
  const [state, setState] = useState(existingUser?.state ?? existingInvite?.state ?? '');
  const [postalCode, setPostalCode] = useState(existingUser?.postalCode ?? existingInvite?.postalCode ?? '');
  const [dateOfBirth, setDateOfBirth] = useState(
    toDateInputValue(existingUser?.dateOfBirth ?? existingInvite?.dateOfBirth),
  );
  const [hireDate, setHireDate] = useState(
    toDateInputValue(existingUser?.hireDate ?? existingInvite?.hireDate),
  );
  const [hourlyPay, setHourlyPay] = useState(
    moneyToInput(existingUser?.hourlyPay ?? existingInvite?.hourlyPay),
  );
  const [annualSalary, setAnnualSalary] = useState(
    moneyToInput(existingUser?.annualSalary ?? existingInvite?.annualSalary),
  );
  const [billableHourlyRate, setBillableHourlyRate] = useState(
    moneyToInput(existingUser?.billableHourlyRate ?? existingInvite?.billableHourlyRate),
  );
  const [hoursWorked, setHoursWorked] = useState<HoursWorkedWeekForm[]>(
    toHoursWorkedForm(existingUser?.hoursWorked ?? existingInvite?.hoursWorked),
  );
  const [expandedWeeks, setExpandedWeeks] = useState<boolean[]>([true]);
  const [editingDay, setEditingDay] = useState<{weekIndex: number; dayIndex: number} | null>(null);
  const [weekSearchFrom, setWeekSearchFrom] = useState('');
  const [weekSearchTo, setWeekSearchTo] = useState('');
  const [groupId, setGroupId] = useState<number | null>(initialGroupId);
  const [emailInvite, setEmailInvite] = useState(isNew);
  const [textInvite, setTextInvite] = useState(false);
  const [permsOpen, setPermsOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [confirmActiveOpen, setConfirmActiveOpen] = useState(false);

  useEffect(() => {
    const nextName = splitName(existingUser?.name ?? existingInvite?.name ?? '');
    setFirstName(nextName.first);
    setLastName(nextName.last);
    setEmail(existingUser?.email ?? existingInvite?.email ?? '');
    setPhoneNumber(formatPhoneNumber(existingUser?.phoneNumber ?? existingInvite?.phoneNumber ?? ''));
    setAddress(existingUser?.address ?? existingInvite?.address ?? '');
    setCity(existingUser?.city ?? existingInvite?.city ?? '');
    setState(existingUser?.state ?? existingInvite?.state ?? '');
    setPostalCode(existingUser?.postalCode ?? existingInvite?.postalCode ?? '');
    setDateOfBirth(toDateInputValue(existingUser?.dateOfBirth ?? existingInvite?.dateOfBirth));
    setHireDate(toDateInputValue(existingUser?.hireDate ?? existingInvite?.hireDate));
    setHourlyPay(moneyToInput(existingUser?.hourlyPay ?? existingInvite?.hourlyPay));
    setAnnualSalary(moneyToInput(existingUser?.annualSalary ?? existingInvite?.annualSalary));
    setBillableHourlyRate(moneyToInput(existingUser?.billableHourlyRate ?? existingInvite?.billableHourlyRate));
    setHoursWorked(toHoursWorkedForm(existingUser?.hoursWorked ?? existingInvite?.hoursWorked));
    setExpandedWeeks([true]);
    setEditingDay(null);
    setWeekSearchFrom('');
    setWeekSearchTo('');
    setGroupId(
      existingUser?.groupIds[0]
        ?? existingInvite?.defaultGroupId
        ?? null,
    );
    setEmailInvite(props.target.kind === 'new');
    setTextInvite(false);
    setPermsOpen(false);
  }, [existingInvite, existingUser, props.target.kind]);

  const fullName = joinName(firstName, lastName);
  const selectedGroup = sortedGroups.find(group => group.id === groupId) ?? null;
  const hasPhoneNumber = phoneDigitCount(phoneNumber) >= 10;
  const canSave = fullName.length > 0 && (email.trim().length > 0 || hasPhoneNumber) && groupId != null;

  const codesByGroupId = useMemo(() => {
    const map = new Map<number, Set<string>>();
    for (const group of sortedGroups) {
      const roleIds = new Set(group.roleIds);
      let matched = props.roles.filter(role => roleIds.has(role.id));
      if (matched.length === 0 && group.systemKey) {
        matched = props.roles.filter(role => role.systemKey === group.systemKey);
      }
      map.set(group.id, new Set(matched.flatMap(role => role.permissionCodes)));
    }
    return map;
  }, [props.roles, sortedGroups]);

  const minLevelByCode = useMemo(() => {
    const map = new Map<string, number>();
    for (const group of sortedGroups) {
      const level = groupLevelNumber(group);
      const codes = codesByGroupId.get(group.id);
      if (!codes) {
        continue;
      }
      for (const code of Array.from(codes)) {
        const current = map.get(code);
        if (current == null || level < current) {
          map.set(code, level);
        }
      }
    }
    return map;
  }, [codesByGroupId, sortedGroups]);

  const selectedPermissions = useMemo(() => {
    if (!selectedGroup) {
      return [];
    }
    const codes = codesByGroupId.get(selectedGroup.id) ?? new Set<string>();
    const byArea = new Map<string, PermissionCatalogItem[]>();
    for (const permission of props.permissions) {
      if (!codes.has(permission.code)) {
        continue;
      }
      const list = byArea.get(permission.featureArea) ?? [];
      list.push(permission);
      byArea.set(permission.featureArea, list);
    }
    return Array.from(byArea.entries()).sort((a, b) => {
      const ai = AREA_ORDER.indexOf(a[0]);
      const bi = AREA_ORDER.indexOf(b[0]);
      return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
    });
  }, [codesByGroupId, props.permissions, selectedGroup]);

  const permissionCount = selectedPermissions.reduce((count, [, items]) => count + items.length, 0);

  const handleSave = async () => {
    if (groupId == null || !fullName || (!email.trim() && !hasPhoneNumber)) {
      enqueueSnackbar('Name, access level, and an email or phone number are required.', {variant: 'error'});
      return;
    }
    if (textInvite && !hasPhoneNumber) {
      enqueueSnackbar('Add a phone number before texting the invite.', {variant: 'error'});
      return;
    }
    const sendEmailInvite = emailInvite && email.trim().length > 0;
    const sendTextInvite = textInvite && hasPhoneNumber;
    setSaving(true);
    const contact = {
      phoneNumber: emptyToNull(phoneNumber),
      address: emptyToNull(address),
      city: emptyToNull(city),
      state: emptyToNull(state),
      postalCode: emptyToNull(postalCode),
      dateOfBirth: emptyToNull(dateOfBirth),
      hireDate: emptyToNull(hireDate),
      hourlyPay: parseMoney(hourlyPay),
      annualSalary: parseMoney(annualSalary),
      billableHourlyRate: parseMoney(billableHourlyRate),
      hoursWorked: serializeHoursWorked(hoursWorked),
    };
    try {
      let assignmentId: number | null = null;
      if (props.target.kind === 'user') {
        const groupIds = groupIdsForUserSave(
          props.target.user.groupIds,
          groupId,
          initialGroupId,
        );
        await updateRbacUser(navigate, props.target.user.id, {
          name: fullName,
          ...(groupIds ? {groupIds} : {}),
          ...contact,
        });
      } else if (props.target.kind === 'invite') {
        await updateCompanyUserAssignment(navigate, props.target.invite.id, {
          name: fullName,
          email: emptyToNull(email),
          defaultGroupId: groupId,
          ...contact,
        });
        assignmentId = props.target.invite.id;
      } else {
        const created = await addCompanyUserAssignment(navigate, {
          name: fullName,
          email: emptyToNull(email),
          defaultGroupId: groupId,
          ...contact,
        });
        assignmentId = created.id;
      }

      if (assignmentId != null && (sendEmailInvite || sendTextInvite)) {
        try {
          await sendCompanyUserAssignmentInvite(navigate, assignmentId, {
            emailInvite: sendEmailInvite,
            textInvite: sendTextInvite,
          });
          enqueueSnackbar(
            isNew ? 'Person added. Invite sent.' : 'Person saved. Invite sent.',
            {variant: 'success'},
          );
        } catch (inviteError) {
          logError('Error sending team invite', {target: props.target.kind}, inviteError as Error);
          enqueueSnackbar(
            `Person saved, but the invite could not be sent: ${errorMessage(inviteError)}`,
            {variant: 'error', autoHideDuration: 7000},
          );
        }
      } else {
        enqueueSnackbar(isNew ? 'Person added.' : 'Person saved.', {variant: 'success'});
      }
      props.onSaved();
    } catch (error) {
      logError('Error saving person', {target: props.target.kind}, error as Error);
      enqueueSnackbar(`Error saving person: ${errorMessage(error)}`, {variant: 'error', autoHideDuration: 5000});
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      if (props.target.kind === 'user') {
        await deleteRbacUser(navigate, props.target.user.id);
      } else if (props.target.kind === 'invite') {
        await deleteCompanyUserAssignment(navigate, props.target.invite.id);
      }
      enqueueSnackbar('Person removed.', {variant: 'success'});
      setConfirmDeleteOpen(false);
      props.onSaved();
    } catch (error) {
      logError('Error removing person', {target: props.target.kind}, error as Error);
      enqueueSnackbar(`Error removing person: ${errorMessage(error)}`, {variant: 'error', autoHideDuration: 7000});
    } finally {
      setSaving(false);
    }
  };

  const handleSetActive = async (active: boolean) => {
    setSaving(true);
    try {
      if (props.target.kind === 'user') {
        await setRbacUserActive(navigate, props.target.user.id, active);
      } else if (props.target.kind === 'invite') {
        await setCompanyUserAssignmentActive(navigate, props.target.invite.id, active);
      }
      enqueueSnackbar(active ? 'Person is active.' : 'Person is inactive.', {variant: 'success'});
      setConfirmActiveOpen(false);
      props.onSaved();
    } catch (error) {
      logError('Error updating person status', {target: props.target.kind, active}, error as Error);
      enqueueSnackbar(`Error updating person: ${errorMessage(error)}`, {variant: 'error', autoHideDuration: 7000});
    } finally {
      setSaving(false);
    }
  };

  const title = isNew ? 'Add New Team Member' : (fullName || email || phoneNumber || 'Team Member');

  const updateHoursDay = (weekIndex: number, dayIndex: number, patch: Partial<HoursWorkedDayForm>) => {
    setHoursWorked(weeks => weeks.map((week, index) => (
      index === weekIndex
        ? {...week, days: week.days.map((day, di) => (di === dayIndex ? {...day, ...patch} : day))}
        : week
    )));
  };

  const addHoursWeek = () => {
    setHoursWorked(weeks => weeks.length >= 12 ? weeks : [...weeks, emptyWeek(nextWeekStart(weeks))]);
    setExpandedWeeks(flags => flags.length >= 12 ? flags : [...flags, true]);
    setEditingDay(null);
  };

  const removeHoursWeek = (weekIndex: number) => {
    setHoursWorked(weeks => {
      const next = weeks.filter((_, index) => index !== weekIndex);
      return next.length > 0 ? next : [emptyWeek()];
    });
    setExpandedWeeks(flags => {
      const next = flags.filter((_, index) => index !== weekIndex);
      return next.length > 0 ? next : [true];
    });
    setEditingDay(cell => {
      if (!cell) {
        return null;
      }
      if (cell.weekIndex === weekIndex) {
        return null;
      }
      if (cell.weekIndex > weekIndex) {
        return {weekIndex: cell.weekIndex - 1, dayIndex: cell.dayIndex};
      }
      return cell;
    });
  };

  const toggleWeekExpanded = (weekIndex: number) => {
    const isOpen = expandedWeeks[weekIndex] ?? false;
    setExpandedWeeks(flags => {
      const next = hoursWorked.map((_, index) => flags[index] ?? false);
      next[weekIndex] = !next[weekIndex];
      return next;
    });
    if (isOpen) {
      setEditingDay(cell => (cell?.weekIndex === weekIndex ? null : cell));
    }
  };

  const weekHoursTotal = (week: HoursWorkedWeekForm): number =>
    week.days.reduce((sum, day) => sum + (Number(day.hours) || 0), 0);

  const visibleWeeks = hoursWorked
    .map((week, weekIndex) => ({week, weekIndex}))
    .filter(({week}) => weekOverlapsRange(week, weekSearchFrom, weekSearchTo));

  const applyParsedAddress = (parsed: ParsedAddressFields) => {
    setAddress(current => parsed.street || current);
    if (parsed.city) {
      setCity(parsed.city);
    }
    const stateCode = parsed.state.trim().toUpperCase();
    if (US_STATES.some(option => option.code === stateCode)) {
      setState(stateCode);
    }
    if (parsed.postalCode) {
      setPostalCode(parsed.postalCode.replace(/[^\d-]/g, '').slice(0, 10));
    }
  };

  return (
    <>
      <Card
        ref={ref as React.Ref<HTMLDivElement>}
        sx={props.page ? {outline: 'none', boxShadow: 'none', background: 'transparent', color: 'text.primary', mx: -2, mt: -2} : {
          maxWidth: 780,
          width: 'calc(100% - 32px)',
          maxHeight: '90vh',
          mx: 'auto',
          my: {xs: 1, sm: 4},
          overflowY: 'auto',
          outline: 'none',
          borderRadius: '14px',
          boxShadow: 8,
          color: 'text.primary',
        }}
        tabIndex={-1}
      >
        <Box sx={{px: {xs: 2, sm: 3}, pt: 3, pb: 3}}>
          <Box sx={{display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 1}}>
            <Typography id="person-editor-title" variant="h4" sx={{lineHeight: 1.15, letterSpacing: '-0.2px'}}>
              {title}
            </Typography>
            {!isNew && (
              <Chip
                size="small"
                label={membershipStatus}
                color={membershipStatus === 'Active' ? 'success' : membershipStatus === 'Inactive' ? 'warning' : 'default'}
              />
            )}
          </Box>
          <Typography color="text.secondary" sx={{fontSize: 15, mb: 3}}>
            {isNew
              ? 'They join this company the first time they sign in with this email or phone number.'
              : !isActive
                ? 'Inactive. They stay on jobs and other records, and they cannot sign in until you make them active.'
                : isSignedIn
                  ? (email.trim()
                    ? 'Change their name or access level. Email is their sign-in account.'
                    : 'Change their name or access level. Phone is their sign-in account.')
                  : 'Pending. They join and receive this access level the first time they sign in.'}
          </Typography>

          <Section title="Details">
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {xs: '1fr', sm: '1fr 1fr'},
                gap: '18px 12px',
              }}
            >
              <TextField
                autoFocus
                label="First name"
                value={firstName}
                onChange={event => setFirstName(event.target.value)}
                required
                fullWidth
                sx={fieldSx}
              />
              <TextField
                label="Last name"
                value={lastName}
                onChange={event => setLastName(event.target.value)}
                fullWidth
                sx={fieldSx}
              />
              <TextField
                label="Email"
                type="email"
                value={email}
                onChange={event => setEmail(event.target.value)}
                fullWidth
                disabled={isSignedIn}
                helperText={isSignedIn
                  ? 'Email cannot be changed here.'
                  : 'Optional if they have a phone number.'}
                sx={{...fieldSx, gridColumn: {sm: '1 / -1'}}}
              />
              <TextField
                label="Phone number"
                type="tel"
                value={phoneNumber}
                onChange={event => {
                  const next = formatPhoneNumber(event.target.value);
                  setPhoneNumber(next);
                  if (phoneDigitCount(next) < 10) {
                    setTextInvite(false);
                  }
                }}
                fullWidth
                inputProps={{inputMode: 'tel', autoComplete: 'tel'}}
                sx={fieldSx}
              />
              <TextField
                label="Date of birth"
                type="date"
                value={dateOfBirth}
                onChange={event => setDateOfBirth(event.target.value)}
                fullWidth
                InputLabelProps={{shrink: true}}
                sx={fieldSx}
              />
              <Box sx={{gridColumn: {sm: '1 / -1'}}}>
                <AddressAutocompleteField
                  value={address}
                  onChange={setAddress}
                  onAddressPicked={applyParsedAddress}
                  sx={fieldSx}
                />
              </Box>
              <TextField
                label="City"
                value={city}
                onChange={event => setCity(event.target.value)}
                fullWidth
                sx={fieldSx}
              />
              <TextField
                select
                label="State"
                value={state}
                onChange={event => setState(event.target.value)}
                fullWidth
                sx={fieldSx}
              >
                <MenuItem value="">
                  <em>Select</em>
                </MenuItem>
                {US_STATES.map(option => (
                  <MenuItem key={option.code} value={option.code}>
                    {option.code} — {option.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Zip code"
                value={postalCode}
                onChange={event => setPostalCode(event.target.value.replace(/[^\d-]/g, '').slice(0, 10))}
                fullWidth
                inputProps={{inputMode: 'numeric', autoComplete: 'postal-code'}}
                sx={fieldSx}
              />
            </Box>
          </Section>

          <Section title="Payroll" note="Hire date, pay, and scheduled hours.">
            <Box>
              <Typography sx={{fontSize: 15.5, fontWeight: 700, mb: 1.5}}>Employment Dates</Typography>
              <TextField
                label="Hire date"
                type="date"
                value={hireDate}
                onChange={event => setHireDate(event.target.value)}
                fullWidth
                InputLabelProps={{shrink: true}}
                sx={fieldSx}
              />
            </Box>

            <Box sx={{mt: 2.5}}>
              <Typography sx={{fontSize: 15.5, fontWeight: 700, mb: 1.5}}>Pay Rate</Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {xs: '1fr', sm: '1fr 1fr'},
                  gap: '18px 12px',
                }}
              >
                <TextField
                  label="Hourly pay"
                  value={hourlyPay}
                  onChange={event => setHourlyPay(event.target.value.replace(/[^\d.]/g, ''))}
                  fullWidth
                  inputProps={{inputMode: 'decimal'}}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                  sx={fieldSx}
                />
                <TextField
                  label="Annual salary"
                  value={annualSalary}
                  onChange={event => setAnnualSalary(event.target.value.replace(/[^\d.]/g, ''))}
                  fullWidth
                  inputProps={{inputMode: 'decimal'}}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                  sx={fieldSx}
                />
              </Box>
            </Box>

            <Box sx={{mt: 2.5}}>
              <Typography sx={{fontSize: 15.5, fontWeight: 700, mb: 0.5}}>Hours worked</Typography>
              <Typography color="text.secondary" sx={{fontSize: 13.5, mb: 1.5}}>
                Expand a week to see each day. Edit hours on the right.
              </Typography>
              {visibleWeeks.length === 0 ? (
                <Typography color="text.secondary" sx={{fontSize: 13.5, mb: 1.5}}>
                  No weeks in this date range.
                </Typography>
              ) : (
                visibleWeeks.map(({week, weekIndex}) => {
                const total = weekHoursTotal(week);
                const expanded = expandedWeeks[weekIndex] ?? false;
                return (
                  <Box
                    key={week.startDate || weekIndex}
                    sx={{
                      bgcolor: 'background.paper',
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: '8px',
                      mb: 1.5,
                      overflow: 'hidden',
                    }}
                  >
                    <Box sx={{display: 'flex', alignItems: 'stretch'}}>
                      <Box
                        component="button"
                        type="button"
                        onClick={() => toggleWeekExpanded(weekIndex)}
                        aria-expanded={expanded}
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          px: 1.5,
                          py: 1.35,
                          textAlign: 'left',
                          cursor: 'pointer',
                          border: 'none',
                          background: 'transparent',
                          fontFamily: 'inherit',
                          color: 'text.primary',
                          '&:hover': {bgcolor: theme => alpha(theme.palette.primary.main, 0.04)},
                        }}
                      >
                        <KeyboardArrowDownIcon
                          sx={{
                            fontSize: 22,
                            color: 'text.secondary',
                            flex: 'none',
                            transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                            transition: 'transform 0.15s ease',
                          }}
                        />
                        <Typography sx={{fontWeight: 700, fontSize: 15, flex: 1}}>
                          {formatWeekRange(week.startDate)}
                        </Typography>
                        <Typography color="text.secondary" sx={{fontSize: 13.5, fontWeight: 700, flex: 'none'}}>
                          {formatHoursLabel(total)}
                        </Typography>
                      </Box>
                      {hoursWorked.length > 1 && (
                        <Button
                          onClick={() => removeHoursWeek(weekIndex)}
                          sx={{minWidth: 0, px: 1.25, textTransform: 'none', color: 'error.main', fontWeight: 700}}
                        >
                          Remove
                        </Button>
                      )}
                    </Box>
                    <Collapse in={expanded} unmountOnExit={false}>
                      <Box sx={{borderTop: 1, borderColor: 'divider', px: 1.5, py: 0.5}}>
                        {week.days.map((day, dayIndex) => {
                          const isEditing = editingDay?.weekIndex === weekIndex
                            && editingDay.dayIndex === dayIndex;
                          return (
                            <Box
                              key={day.day}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                py: 0.85,
                                borderTop: dayIndex === 0 ? 'none' : 1,
                                borderColor: 'divider',
                              }}
                            >
                              <Typography sx={{fontSize: 14.5, fontWeight: 500, flex: 1}}>
                                {weekdayLabel(day.day)}
                              </Typography>
                              {isEditing ? (
                                <TextField
                                  autoFocus
                                  label="Hours"
                                  value={day.hours}
                                  onChange={event => updateHoursDay(weekIndex, dayIndex, {
                                    hours: event.target.value.replace(/[^\d.]/g, ''),
                                  })}
                                  onKeyDown={event => {
                                    if (event.key === 'Enter' || event.key === 'Escape') {
                                      event.preventDefault();
                                      setEditingDay(null);
                                    }
                                  }}
                                  size="small"
                                  inputProps={{inputMode: 'decimal'}}
                                  sx={{...fieldSx, width: 110}}
                                />
                              ) : (
                                <Typography color="text.secondary" sx={{fontSize: 13.5, fontWeight: 700, mr: 0.5}}>
                                  {formatHoursLabel(day.hours)}
                                </Typography>
                              )}
                              <Button
                                onClick={() => setEditingDay(
                                  isEditing ? null : {weekIndex, dayIndex},
                                )}
                                sx={{
                                  minWidth: 0,
                                  px: 1.5,
                                  py: 0.4,
                                  textTransform: 'none',
                                  fontWeight: 700,
                                  fontSize: 13,
                                  color: 'primary.main',
                                  border: 1,
                                  borderColor: 'primary.main',
                                  borderRadius: '999px',
                                  bgcolor: 'background.paper',
                                  '&:hover': {bgcolor: theme => alpha(theme.palette.primary.main, 0.16)},
                                }}
                              >
                                {isEditing ? 'Done' : 'Edit'}
                              </Button>
                            </Box>
                          );
                        })}
                      </Box>
                    </Collapse>
                  </Box>
                );
              })
              )}
              {hoursWorked.length < 12 && (
                <Button
                  onClick={addHoursWeek}
                  sx={{textTransform: 'none', fontWeight: 700, color: 'primary.main', px: 0}}
                >
                  + Add week
                </Button>
              )}
              <Box
                sx={{
                  mt: 1.5,
                  display: 'grid',
                  gridTemplateColumns: {xs: '1fr', sm: '1fr 1fr'},
                  gap: '12px',
                }}
              >
                <TextField
                  label="From date"
                  type="date"
                  value={weekSearchFrom}
                  onChange={event => setWeekSearchFrom(event.target.value)}
                  fullWidth
                  InputLabelProps={{shrink: true}}
                  inputProps={{max: weekSearchTo || undefined}}
                  sx={fieldSx}
                />
                <TextField
                  label="To date"
                  type="date"
                  value={weekSearchTo}
                  onChange={event => setWeekSearchTo(event.target.value)}
                  fullWidth
                  InputLabelProps={{shrink: true}}
                  inputProps={{min: weekSearchFrom || undefined}}
                  sx={fieldSx}
                />
              </Box>
            </Box>
          </Section>

          <Section title="Billable Rate" note="Rate billed to clients for this person's time.">
            <TextField
              label="Billable hourly rate"
              value={billableHourlyRate}
              onChange={event => setBillableHourlyRate(event.target.value.replace(/[^\d.]/g, ''))}
              fullWidth
              inputProps={{inputMode: 'decimal'}}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
              sx={fieldSx}
            />
          </Section>

          <Section title="Permissions" note="Pick the level that fits this team member.">
            <Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
              {sortedGroups.map(group => {
                const selected = group.id === groupId;
                return (
                  <Box
                    key={group.id}
                    component="button"
                    type="button"
                    onClick={() => setGroupId(group.id)}
                    sx={theme => ({
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      width: '100%',
                      textAlign: 'left',
                      cursor: 'pointer',
                      px: 1.75,
                      py: 1.5,
                      borderRadius: '8px',
                      border: '1px solid',
                      borderColor: selected ? 'primary.main' : 'divider',
                      bgcolor: selected ? alpha(theme.palette.primary.main, 0.16) : 'background.paper',
                      boxShadow: selected ? `inset 0 0 0 1px ${theme.palette.primary.main}` : 'none',
                      fontFamily: 'inherit',
                      fontSize: '15.5px',
                      color: 'text.primary',
                      '&:hover': {borderColor: 'primary.main'},
                    })}
                  >
                    <Box
                      sx={{
                        width: 17,
                        height: 17,
                        borderRadius: '50%',
                        flex: 'none',
                        border: 2,
                        borderColor: selected ? 'primary.main' : 'action.disabled',
                        bgcolor: 'background.paper',
                        display: 'grid',
                        placeItems: 'center',
                      }}
                    >
                      {selected && (
                        <Box sx={{width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.main'}} />
                      )}
                    </Box>
                    {(() => {
                      const label = parseGroupLabel(group.name);
                      return (
                        <Box>
                          <Box component="span" sx={{fontWeight: 700}}>
                            {label.title}
                            {label.detail ? (
                              <Box component="span" sx={{color: 'text.secondary', fontWeight: 400}}> {label.detail}</Box>
                            ) : null}
                          </Box>
                        </Box>
                      );
                    })()}
                  </Box>
                );
              })}
            </Box>

            <Box sx={{mt: 2.25}}>
              {groupId == null ? (
                <Typography color="text.secondary" sx={{fontSize: 14}}>
                  Pick a level to see what it comes with.
                </Typography>
              ) : (
                <Box
                  component="button"
                  type="button"
                  onClick={() => setPermsOpen(open => !open)}
                  sx={theme => ({
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1.75,
                    textAlign: 'left',
                    cursor: 'pointer',
                    px: 2,
                    py: 1.6,
                    borderRadius: '8px',
                    border: '1px solid',
                    borderColor: 'primary.main',
                    bgcolor: alpha(theme.palette.primary.main, 0.16),
                    fontFamily: 'inherit',
                    color: 'text.primary',
                    '&:hover': {bgcolor: alpha(theme.palette.primary.main, 0.24)},
                  })}
                >
                  <Box>
                    <Box sx={{display: 'block', fontSize: '15.5px', fontWeight: 700}}>View permissions</Box>
                    <Box sx={{display: 'block', fontSize: 13, color: 'text.secondary', mt: 0.25}}>
                      See everything {selectedGroup?.name ?? 'this level'} can do
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.75,
                      flex: 'none',
                      fontSize: 13,
                      fontWeight: 700,
                      color: 'primary.dark',
                      bgcolor: 'background.paper',
                      border: 1,
                      borderColor: 'primary.main',
                      borderRadius: '999px',
                      px: 1.5,
                      py: 0.75,
                    }}
                  >
                    {permsOpen ? 'Hide' : 'Open'}
                    <Box component="span" sx={{transform: permsOpen ? 'rotate(90deg)' : 'none', display: 'inline-block'}}>›</Box>
                  </Box>
                </Box>
              )}
            </Box>

            <Collapse in={permsOpen && groupId != null}>
              <Box
                sx={theme => ({
                  mt: 2,
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: '10px',
                  p: 2,
                })}
              >
                <Typography color="text.secondary" sx={{fontSize: 13.5, mb: 1.5}}>
                  Everything {selectedGroup?.name} can do.
                </Typography>
                <Box
                  sx={{
                    bgcolor: 'background.paper',
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: '8px',
                    px: 2,
                    pb: 1.5,
                  }}
                >
                  <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 1, py: 1.5, borderBottom: 1, borderColor: 'divider'}}>
                    <Typography variant="subtitle1">{selectedGroup?.name}</Typography>
                    <Chip size="small" color="primary" variant="outlined" label={`${permissionCount} on`} />
                  </Box>
                  {selectedPermissions.map(([area, items]) => (
                    <Box key={area} sx={{pt: 1.75}}>
                      <Typography color="primary.main" sx={{fontSize: 11.5, fontWeight: 700, letterSpacing: '0.6px', textTransform: 'uppercase', mb: 1}}>
                        {areaLabel(area)}
                      </Typography>
                      {items.map(item => {
                        const minLevel = minLevelByCode.get(item.code);
                        return (
                          <Box key={item.code} sx={{pl: 1.75, py: 0.75, position: 'relative', '&:before': {
                            content: '""',
                            position: 'absolute',
                            left: 0,
                            top: 14,
                            width: 7,
                            height: 7,
                            borderRadius: '50%',
                            bgcolor: 'primary.main',
                          }}}>
                            <Typography sx={{fontSize: 15, fontWeight: 500, lineHeight: 1.3}}>
                              {item.description}
                              {minLevel != null && minLevel < 999 ? (
                                <Box component="span" sx={{fontSize: '12.5px', fontWeight: 400, color: 'text.secondary', ml: 0.75, whiteSpace: 'nowrap'}}>
                                  (Level {minLevel})
                                </Box>
                              ) : null}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  ))}
                  {permissionCount === 0 && (
                    <Typography color="text.secondary" sx={{mt: 1.5, fontSize: 14}}>Nothing is turned on at this level.</Typography>
                  )}
                </Box>
                <Button
                  fullWidth
                  onClick={() => setPermsOpen(false)}
                  sx={{
                    mt: 1.5,
                    borderRadius: '999px',
                    border: 1,
                    borderColor: 'divider',
                    color: 'primary.main',
                    fontWeight: 700,
                    textTransform: 'none',
                    '&:hover': {bgcolor: theme => alpha(theme.palette.primary.main, 0.08)},
                  }}
                >
                  Close permissions list
                </Button>
              </Box>
            </Collapse>
          </Section>

          {!isSignedIn && isActive && (
            <Section title="Login Invite" note="They set up their own login from the invite.">
              <Typography color="text.secondary" sx={{fontSize: 14.5, lineHeight: 1.45, mb: 1.5}}>
                Save this person, then have them sign in with{' '}
                <strong>
                  {email.trim() || phoneNumber || 'this email or phone number'}
                </strong>.
                They will join the company and receive the access level you picked.
              </Typography>
              <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.5}}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={emailInvite}
                      onChange={event => setEmailInvite(event.target.checked)}
                      disabled={!email.trim()}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography sx={{fontSize: 15, fontWeight: 700}}>Email the invite</Typography>
                      <Typography color="text.secondary" sx={{fontSize: 13}}>
                        {email.trim()
                          ? `Send a sign-in link to ${email.trim()}.`
                          : 'Add an email to send the invite.'}
                      </Typography>
                    </Box>
                  }
                  sx={{alignItems: 'flex-start', ml: 0, mr: 0}}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={textInvite}
                      onChange={event => setTextInvite(event.target.checked)}
                      disabled={!hasPhoneNumber}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography sx={{fontSize: 15, fontWeight: 700}}>Text the invite</Typography>
                      <Typography color="text.secondary" sx={{fontSize: 13}}>
                        {hasPhoneNumber
                          ? `Send a sign-in link to ${phoneNumber}.`
                          : 'Add a phone number to text the invite.'}
                      </Typography>
                    </Box>
                  }
                  sx={{alignItems: 'flex-start', ml: 0, mr: 0}}
                />
              </Box>
            </Section>
          )}

          {!isNew && !canDelete && (
            <Typography color="text.secondary" sx={{mt: 3, fontSize: 14.5, lineHeight: 1.45}}>
              {isSelf
                ? 'You cannot remove yourself.'
                : 'This person is included in appointments, estimates, or jobs, so they can\'t be deleted. Make them inactive instead.'}
            </Typography>
          )}
          <Box sx={{display: 'flex', gap: 1.5, justifyContent: 'flex-end', flexWrap: 'wrap', mt: 3}}>
            {!isNew && !isSelf && (
              <Button
                disabled={saving}
                color={isActive ? 'warning' : 'success'}
                variant="outlined"
                onClick={() => setConfirmActiveOpen(true)}
                sx={{
                  borderRadius: '999px',
                  px: 3,
                  py: 1.25,
                  fontWeight: 700,
                  textTransform: 'none',
                }}
              >
                {isActive ? 'Make inactive' : 'Make active'}
              </Button>
            )}
            {!isNew && canDelete && (
              <Button
                disabled={saving || isSelf}
                color="error"
                variant="outlined"
                onClick={() => setConfirmDeleteOpen(true)}
                sx={{
                  borderRadius: '999px',
                  px: 3,
                  py: 1.25,
                  fontWeight: 700,
                  textTransform: 'none',
                }}
              >
                {isSelf ? 'You cannot remove yourself' : 'Remove'}
              </Button>
            )}
            <Button
              onClick={props.onClose}
              disabled={saving}
              variant="outlined"
              color="inherit"
              sx={{
                borderRadius: '999px',
                px: 3.25,
                py: 1.25,
                fontWeight: 700,
                textTransform: 'none',
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => void handleSave()}
              disabled={saving || !canSave}
              variant="contained"
              sx={{
                borderRadius: '999px',
                px: 3.25,
                py: 1.25,
                fontWeight: 700,
                textTransform: 'none',
              }}
            >
              {saving ? <CircularProgress size={22} color="inherit" /> : (isNew ? 'Save Team Member' : 'Save Changes')}
            </Button>
          </Box>
        </Box>
      </Card>
      <Dialog open={confirmActiveOpen} onClose={() => setConfirmActiveOpen(false)}>
        <DialogTitle>{isActive ? 'Make this person inactive?' : 'Make this person active?'}</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary">
            {isActive
              ? `${fullName || email || phoneNumber} will stay on jobs and other records, and they will not be able to sign in until you make them active again.`
              : `${fullName || email || phoneNumber} will be able to sign in again, and they can be added to new jobs.`}
          </Typography>
        </DialogContent>
        <DialogActions sx={{p: 2, gap: 1}}>
          <Button onClick={() => setConfirmActiveOpen(false)} disabled={saving} sx={{textTransform: 'none'}}>Cancel</Button>
          <Button
            disabled={saving}
            onClick={() => void handleSetActive(!isActive)}
            sx={{textTransform: 'none'}}
            color={isActive ? 'warning' : 'success'}
            variant="contained"
          >
            {isActive ? 'Make inactive' : 'Make active'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)}>
        <DialogTitle>Remove this person?</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary">
            {isSignedIn
              ? `${fullName || email || phoneNumber} will lose access to this company. They will not be able to sign in again unless you add them back.`
              : `The invite for ${fullName || email || phoneNumber} will be removed. They will not be able to join until you add them again.`}
          </Typography>
        </DialogContent>
        <DialogActions sx={{p: 2, gap: 1}}>
          <Button onClick={() => setConfirmDeleteOpen(false)} disabled={saving} sx={{textTransform: 'none'}}>Cancel</Button>
          <Button
            disabled={saving}
            onClick={() => void handleDelete()}
            sx={{textTransform: 'none'}}
            color="error"
            variant="contained"
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
});

function Section({title, note, children}: {title: string; note?: string; children: React.ReactNode}) {
  return (
    <Box
      sx={theme => ({
        bgcolor: alpha(theme.palette.primary.main, 0.04),
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '12px',
        mb: 2.25,
      })}
    >
      <Box sx={{display: 'flex', gap: 1.5, alignItems: 'flex-start', px: 2.5, pt: 2, pb: 1.75, borderBottom: 1, borderColor: 'divider'}}>
        <Box sx={{width: 4, alignSelf: 'stretch', minHeight: 26, bgcolor: 'primary.main', borderRadius: '3px', flex: 'none'}} />
        <Box>
          <Typography variant="h6" sx={{fontWeight: 400, m: 0}}>{title}</Typography>
          {note ? <Typography color="text.secondary" sx={{mt: 0.5, fontSize: 13.5}}>{note}</Typography> : null}
        </Box>
      </Box>
      <Box sx={{p: 2.5}}>{children}</Box>
    </Box>
  );
}

PersonDetailComponent.displayName = 'PersonDetailComponent';

export default PersonDetailComponent;
